import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookieBite } from '@fortawesome/pro-light-svg-icons/faCookieBite';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { openTrackingConsent } from '../../../analytics/consent/trackingConsent';
import { useTrackingConsent } from '../../../analytics/consent/useTrackingConsent';
import { legalMessages } from './legalMessages';
import { Panel, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

const consentMessages = defineMessages({
  cookieSettings: { id: 'app.sml.settings.cookie_settings', defaultMessage: 'Cookie settings' },
  accepted: { id: 'app.sml.settings.cookie_settings.accepted', defaultMessage: 'Accepted' },
  rejected: { id: 'app.sml.settings.cookie_settings.rejected', defaultMessage: 'Rejected' },
  notSet: { id: 'app.sml.settings.cookie_settings.not_set', defaultMessage: 'Not set' },
});

/**
 * Cookie settings for this browser (design music24-consent a5, settings-redesign e1, e2): the stored
 * choice, and a button that opens the consent bar again.
 */
const CookieSettingsCardComponent: React.FC = () => {
  const intl = useIntl();
  const consent = useTrackingConsent();
  const description =
    consent === 'granted'
      ? legalMessages.cookieAccepted
      : consent === 'denied'
        ? legalMessages.cookieRejected
        : legalMessages.cookieNotSet;

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(legalMessages.privacyChoices)}
        subtitle={intl.formatMessage(legalMessages.forThisBrowser)}
      />
      <div className={cards.row}>
        <span className={`${cards.rowIcon} ${cards.rowIconBrand}`}>
          <FontAwesomeIcon icon={faCookieBite} />
        </span>
        <div className={cards.rowText}>
          <span className={cards.rowTitle}>{intl.formatMessage(consentMessages.cookieSettings)}</span>
          <span className={cards.rowSub}>{intl.formatMessage(description)}</span>
        </div>
        <div className={cards.rowEnd}>
          {consent === 'granted' && (
            <span className={`${cards.pill} ${cards.pillPos}`}>
              <FontAwesomeIcon icon={faCheck} />
              {intl.formatMessage(consentMessages.accepted)}
            </span>
          )}
          {consent === 'denied' && <span className={cards.pill}>{intl.formatMessage(consentMessages.rejected)}</span>}
          {consent !== 'granted' && consent !== 'denied' && (
            <span className={cards.pill}>{intl.formatMessage(consentMessages.notSet)}</span>
          )}
          <Button
            color={consent === 'granted' || consent === 'denied' ? 'secondary' : 'primary'}
            outline={consent === 'granted' || consent === 'denied'}
            size="sm"
            onClick={openTrackingConsent}
          >
            {intl.formatMessage(
              consent === 'granted' || consent === 'denied' ? legalMessages.change : legalMessages.choose
            )}
          </Button>
        </div>
      </div>
    </Panel>
  );
};

export const CookieSettingsCard = TypedMemo(CookieSettingsCardComponent);
